import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Send, User, Clock, Mail, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function JobNotesSection({ jobId, job }) {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newNote, setNewNote] = useState({
    content: '',
    note_type: 'internal',
    send_email: false
  });

  // Determine email recipients based on job data
  const hasAssignedContractor = job?.assigned_contractor_id || job?.assigned_contractor_name;
  const hasClient = job?.client_id;
  const contractorName = job?.assigned_contractor_name || 'No contractor assigned';
  const clientName = job?.client_name || 'Client';

  useEffect(() => {
    if (jobId) {
      fetchNotes();
    }
  }, [jobId]);

  const fetchNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/jobs/${jobId}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(res.data);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newNote.content.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/jobs/${jobId}/notes`, newNote, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewNote({ content: '', note_type: 'internal', send_email: false });
      fetchNotes();
      toast.success(newNote.send_email ? 'Note added and email sent!' : 'Note added');
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getNoteTypeBadge = (type) => {
    const styles = {
      'internal': 'bg-stone-100 text-stone-700',
      'client_communication': 'bg-blue-100 text-blue-700',
      'contractor_communication': 'bg-purple-100 text-purple-700',
    };
    const labels = {
      'internal': 'Internal',
      'client_communication': 'Client',
      'contractor_communication': 'Contractor',
    };
    return <Badge className={styles[type] || 'bg-stone-100'}>{labels[type] || type}</Badge>;
  };

  return (
    <Card data-testid="job-notes-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Notes & Communications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Note Form */}
        <form onSubmit={handleSubmit} className="space-y-3 p-4 bg-stone-50 rounded-lg">
          <Textarea
            placeholder="Add a note or communication..."
            value={newNote.content}
            onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
            rows={3}
            className="resize-none"
            data-testid="note-input"
          />
          
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[150px]">
              <Select
                value={newNote.note_type}
                onValueChange={(val) => setNewNote({ ...newNote, note_type: val, send_email: false })}
              >
                <SelectTrigger data-testid="note-type-select">
                  <SelectValue placeholder="Note type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Note</SelectItem>
                  <SelectItem value="client_communication">Client Communication</SelectItem>
                  <SelectItem value="contractor_communication">Contractor Communication</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {newNote.note_type !== 'internal' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="send_email"
                  checked={newNote.send_email}
                  onCheckedChange={(checked) => setNewNote({ ...newNote, send_email: checked })}
                  disabled={
                    (newNote.note_type === 'contractor_communication' && !hasAssignedContractor) ||
                    (newNote.note_type === 'client_communication' && !hasClient)
                  }
                />
                <Label htmlFor="send_email" className="text-sm flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Send email notification
                </Label>
              </div>
            )}
            
            <Button 
              type="submit" 
              disabled={isSubmitting || !newNote.content.trim()}
              className="bg-[#1F7A78] hover:bg-[#005456]"
              data-testid="add-note-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Adding...' : 'Add Note'}
            </Button>
          </div>
          
          {/* Show email recipient info or warning */}
          {newNote.note_type === 'contractor_communication' && (
            <div className={`text-xs p-2 rounded flex items-center gap-2 ${
              hasAssignedContractor 
                ? 'bg-green-50 text-green-700' 
                : 'bg-amber-50 text-amber-700'
            }`}>
              {hasAssignedContractor ? (
                <>
                  <Mail className="h-3 w-3" />
                  Email will be sent to: <strong>{contractorName}</strong>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3 w-3" />
                  No contractor assigned. Assign a contractor first to send email notifications.
                </>
              )}
            </div>
          )}
          
          {newNote.note_type === 'client_communication' && (
            <div className={`text-xs p-2 rounded flex items-center gap-2 ${
              hasClient 
                ? 'bg-green-50 text-green-700' 
                : 'bg-amber-50 text-amber-700'
            }`}>
              {hasClient ? (
                <>
                  <Mail className="h-3 w-3" />
                  Email will be sent to: <strong>{clientName}</strong>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3 w-3" />
                  No client linked to this job. Link a client first to send email notifications.
                </>
              )}
            </div>
          )}
        </form>

        {/* Notes List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-4 text-stone-500">Loading notes...</div>
          ) : notes.length === 0 ? (
            <div className="text-center py-4 text-stone-400">No notes yet</div>
          ) : (
            notes.map((note) => (
              <div 
                key={note.id} 
                className="p-3 border rounded-lg bg-white"
                data-testid={`note-${note.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-stone-400" />
                    <span className="font-medium text-sm">{note.created_by_name}</span>
                    {getNoteTypeBadge(note.note_type)}
                    {note.email_sent_to && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Sent to {note.email_sent_to}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-stone-400">
                    <Clock className="h-3 w-3" />
                    {note.created_at && format(new Date(note.created_at), 'dd MMM yyyy HH:mm')}
                  </div>
                </div>
                <p className="text-sm text-stone-700 whitespace-pre-wrap">{note.content}</p>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
